import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import Loadable from '../layouts/full/shared/loadable/Loadable';
import AuthRoute from 'src/views/authentication/auth/AuthRoute';
import RoleBasedRedirect from './RoleBasedRedirect';
import useTenantPlanUsage from 'src/hooks/useTenantPlanUsage';

const PlanFeatureGuard = ({ feature, children }) => {
  const { loading, hasApprovalWorkflows } = useTenantPlanUsage();
  if (loading) return <CircularProgress />;
  const allowed = feature === 'approvalWorkflows' ? hasApprovalWorkflows : false;
  if (!allowed) return <Navigate to="/settings/subscription" replace />;
  return children;
};
import RegisterPlanSelection from '../views/authentication/RegisterPlanSelection';
import RegisterPaymentStatus from '../views/authentication/RegisterPaymentStatus';
import RegisterCustomPlanRequest from '../views/authentication/RegisterCustomPlanRequest';

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import('../layouts/full/FullLayout')));
const BlankLayout = Loadable(lazy(() => import('../layouts/blank/BlankLayout')));

/* ****Pages***** */
const QuotesDashboardPage = Loadable(lazy(() => import('../views/quotes/QuotesDashboardPage')));
const ErrorPage = Loadable(lazy(() => import('../views/authentication/Error')));
const Register = Loadable(lazy(() => import('../views/authentication/Register')));
const RegisterVerifyEmail = Loadable(lazy(() => import('../views/authentication/RegisterVerifyEmail')));
const RegisterCompanySetup = Loadable(lazy(() => import('../views/authentication/RegisterCompanySetup')));
const RegisterSelectPlan = Loadable(lazy(() => import('../views/authentication/RegisterSelectPlan')));
const Login = Loadable(lazy(() => import('../views/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('../views/authentication/ResetPassword')));
const Info = Loadable(lazy(() => import('../views/settings/settings-info')));
const ConfigSettings = Loadable(lazy(() => import('../views/settings/config-settings')));
const SubscriptionSettings = Loadable(lazy(() => import('../views/settings/subscription-settings')));
const UsageSettings = Loadable(lazy(() => import('../views/settings/usage-settings')));
const Quotes = Loadable(lazy(() => import('../views/quotes/quotes')));
const Product = Loadable(lazy(() => import('../views/product/product')));
const Leads = Loadable(lazy(()=> import('../views/leads/Leads')));
const Enquiry = Loadable(lazy(()=> import('../views/enquiry/Enquiry')));
const Customers = Loadable(lazy(() => import('../views/customer/customer.jsx')));
const Approvals = Loadable(lazy(() => import('../views/approvals/Approval.jsx')));
const TenantCreate = Loadable(lazy(() => import('../views/settings/tenant-create')));
const AiChatPage = Loadable(lazy(() => import('../views/ai-chat/AiChatPage')));
const IntegrationSettings = Loadable(lazy(() => import('../views/settings/integration-settings')));
const FaqPage = Loadable(lazy(() => import('../views/faq/FaqPage')));

const Router = [
  {
    path: '/',
    element: <FullLayout />,
    children: [
      { path: '/', exact: true, element: <AuthRoute element={<RoleBasedRedirect />} /> },
      { path: 'dashboard', element: <AuthRoute element={<QuotesDashboardPage />} requiredRole='admin' /> },
      { path: 'product', element: <AuthRoute element={<Product />} requiredRole='admin' /> },
      { path: 'customers', element: <AuthRoute element={<Customers />} /> },
      { path: 'quotes', element: <AuthRoute element={<Quotes />} /> },
      { path: 'settings', element: <Navigate to='/settings/info' replace /> },
      { path: 'settings/createUser', element: <Navigate to='/settings/info' replace /> },
      { path: 'settings/info', element: <AuthRoute element={<Info />} /> },
      { path: 'settings/subscription', element: <AuthRoute element={<SubscriptionSettings />} /> },
      { path: 'settings/usage', element: <AuthRoute element={<UsageSettings />} requiredRole='admin' /> },
      { path: 'settings/terms', element: <Navigate to='/settings/config' replace /> },
{ path: 'settings/config', element: <AuthRoute element={<ConfigSettings />} requiredRole='admin' /> },
      { path: 'leads', element: <AuthRoute element={<Leads />} /> },
      { path: 'enquiry', element: <AuthRoute element={<Enquiry />} /> },
      { path: 'approvals', element: <AuthRoute element={<PlanFeatureGuard feature="approvalWorkflows"><Approvals /></PlanFeatureGuard>} /> },
      { path: 'tenant/create', element: <AuthRoute element={<TenantCreate />} /> },
      { path: 'ai-chat', element: <AuthRoute element={<AiChatPage />} /> },
      { path: 'settings/integrations', element: <AuthRoute element={<IntegrationSettings />} requiredRole='admin' /> },
      { path: 'help', element: <AuthRoute element={<FaqPage />} /> },
      { path: '*', element: <Navigate to='/auth/404' /> }
    ]
  },
  {
    path: '/auth',
    element: <BlankLayout />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'reset-password', element: <ResetPassword /> },
      { path: 'verify-email', element: <RegisterVerifyEmail /> },
      { path: 'register', element: <Register /> },
      { path: 'register/verify-email', element: <RegisterVerifyEmail /> },
      { path: 'register/company', element: <RegisterCompanySetup /> },
      { path: 'register/plans', element: <RegisterPlanSelection /> },
      { path: 'register/custom-plan-request', element: <RegisterCustomPlanRequest /> },
      { path: 'register/payment-status', element: <RegisterPaymentStatus /> },
      { path: 'register/select-plan', element: <RegisterSelectPlan /> },
      // { path: 'register/payment-status', element: <RegisterPaymentStatus /> },
      { path: '404', element: <ErrorPage /> },
      { path: '*', element: <Navigate to='/auth/404' /> }
    ]
  }
]


export default Router;
